import { QueryStatus, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ApiError, client } from "./user"
import { ADMIN_IDS, BOND_RATE_LIST, CHAINS } from "../constant"
import { bigAdd, formatNumber } from "../helpers/big"
import { useMemo } from "react"
import { getKeystore } from "./keystore"

export interface Asset {
  asset_id: string
  balance: string
  chain_id: string
  deposit_entries: {
    destination: string
    tag?: string
    properties?: string[]
  }[]
  fee_asset_id: string
  icon_url: string
  name: string
  price_btc: string
  price_usd: string
  symbol: string
  tag: string

  // change_btc: string
  change_usd: string
  // asset_key: string
  // mixin_id: string
  // confirmations: number
  // capitalization: number
  // liquidity: string
  // reserve: string
  // precision: number
  // withdrawal_memo_possibility: "negative" | "positive" | "possible"
  chain_icon_url?: string
  chain_name?: string
  chain_symbol?: string
}

const filterAsset = (asset: Asset) => !/(Pando Rings|4swap |ExinSwap |Pando Innovation ETF|LP Token)/i.test(asset.name)

export const useIsAdmin = () =>
  useMemo(() => {
    const userId = getKeystore()?.userId
    return userId && ADMIN_IDS.includes(userId)
  }, [])

export const useAdminAssets = (userId: string, assetId?: string) =>
  useQuery({
    queryKey: ["admin", "assets", userId, assetId],
    queryFn: async () => {
      let assets: Asset[]
      if (assetId?.length) {
        const asset = await client.get(`admin/assets/${assetId}?user_id=${userId}`).json<Asset>()
        assets = [asset]
      } else {
        assets = await client.get(`admin/assets?user_id=${userId}`).json<Asset[]>()
      }

      assets.sort((a, b) => {
        if (a.asset_id === a.chain_id) return -1
        if (b.asset_id === b.chain_id) return 1

        let result: number = 0
        result = +b.price_usd * +b.balance - +a.price_usd * +a.balance
        if (result !== 0) return result
        result = +b.balance - +a.balance
        if (result !== 0) return result
        result = +b.price_usd - +a.price_usd
        if (result !== 0) return result

        return b.symbol.localeCompare(a.symbol)
      })

      return assets.filter(filterAsset).map((asset) => {
        const chain = CHAINS.find((chain) => chain.chain_id === asset.chain_id)
        if (chain) {
          return Object.assign(asset, {
            chain_icon_url: chain.icon_url,
            chain_name: chain.name,
            chain_symbol: chain.symbol,
          })
        }
        return asset
      })
    },
  })

export const useAssets = () =>
  useQuery({
    queryKey: ["assets"],
    queryFn: async () => {
      const [array, ...force] = await Promise.all([
        client.get("assets").json<Asset[]>(),
        // force fetch
        ...BOND_RATE_LIST.map(({ id }) => client.get(`assets/${id}`).json<Asset>()),
      ])

      const assets: Asset[] = [...[...force, ...array].reduce((acc, item) => acc.set(item.asset_id, item), new Map()).values()]

      assets.sort((a, b) => {
        let result: number = 0
        result = +b.price_usd * +b.balance - +a.price_usd * +a.balance
        if (result !== 0) return result
        result = +b.balance - +a.balance
        if (result !== 0) return result
        result = +b.price_usd - +a.price_usd
        if (result !== 0) return result

        return b.symbol.localeCompare(a.symbol)
      })

      return assets.filter(filterAsset).map((asset) => {
        const chain = CHAINS.find((chain) => chain.chain_id === asset.chain_id)
        if (chain) {
          return Object.assign(asset, {
            chain_icon_url: chain.icon_url,
            chain_name: chain.name,
            chain_symbol: chain.symbol,
          })
        }
        return asset
      })
    },
  })

export const useAsset = (assetId?: string) => {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: ["asset", assetId],
    initialData: () =>
      queryClient
        .getQueryCache()
        .find<Asset[]>(["assets"])
        ?.state.data?.find((item) => item.asset_id === assetId),
    queryFn: async () => {
      if (!assetId) throw new ApiError(404)

      const asset = await client.get(`assets/${assetId}`).json<Asset>()

      if (!filterAsset(asset)) throw new ApiError(404)

      const chain = CHAINS.find((chain) => chain.chain_id === asset.chain_id)
      if (chain) {
        return Object.assign(asset, {
          chain_icon_url: chain.icon_url,
          chain_name: chain.name,
          chain_symbol: chain.symbol,
        })
      }

      return asset
    },
  })
}

export interface Bond {
  asset_id: string
  bond_asset_id: string
  bond_amount: string
  bond_id: string
  created_at: string
  claimed_at?: string
  updated_at?: string
  new_asset_amount: string
  old_asset_amount: string
  trace_id: string
  user_id: string

  mdtSymbol?: string
  asset?: Asset
  bondAsset?: Asset
}

export const useBonds = () => {
  const queryAssets = useAssets()

  const queryBonds = useQuery({
    queryKey: ["bonds"],
    queryFn: () => client.get("bonds").json<Bond[]>(),
  })

  return {
    isLoading: queryAssets.isLoading || queryBonds.isLoading,
    isError: queryAssets.isError || queryBonds.isError,
    status: (queryAssets.isError || queryBonds.isError ? "error" : queryAssets.isLoading || queryBonds.isLoading ? "loading" : "success") as QueryStatus,
    error: queryAssets.error || queryBonds.error,
    refetch: () => {
      queryAssets.isError && queryAssets.refetch()
      queryBonds.isError && queryBonds.refetch()
    },
    data:
      queryBonds.data && queryAssets.data
        ? queryBonds.data.map((item) => {
            const asset = queryAssets.data.find((asset) => asset.asset_id === item.asset_id)
            const bondAsset = queryAssets.data.find((asset) => asset.asset_id === item.bond_asset_id)
            const mdtSymbol = BOND_RATE_LIST.find((bond) => bond.id === item.bond_asset_id)?.mdtSymbol
            return Object.assign(item, { asset, bondAsset, mdtSymbol })
          })
        : undefined,
  }
}
export const useBond = (id?: string) => {
  const queryClient = useQueryClient()

  const queryAssets = useAssets()

  const queryBond = useQuery({
    queryKey: ["bond", id],
    initialData: () =>
      queryClient
        .getQueryCache()
        .find<Bond[]>(["bonds"])
        ?.state.data?.find((item) => item.bond_id === id),
    queryFn: () => {
      if (!id) throw new Error("Bond id is required")

      return client.get(`bonds/${id}`).json<Bond>()
    },
  })

  return {
    isLoading: queryAssets.isLoading || queryBond.isLoading,
    isError: queryAssets.isError || queryBond.isError,
    status: (queryAssets.isError || queryBond.isError ? "error" : queryAssets.isLoading || queryBond.isLoading ? "loading" : "success") as QueryStatus,
    error: queryAssets.error || queryBond.error,
    refetch: () => {
      queryAssets.isError && queryAssets.refetch()
      queryBond.isError && queryBond.refetch()
    },
    data:
      queryBond.data && queryAssets.data
        ? Object.assign(queryBond.data, {
            mdtSymbol: BOND_RATE_LIST.find((bond) => bond.id === queryBond.data?.bond_asset_id)?.mdtSymbol,
            asset: queryAssets.data.find((asset) => asset.asset_id === queryBond.data?.asset_id),
            bondAsset: queryAssets.data.find((asset) => asset.asset_id === queryBond.data?.bond_asset_id),
          })
        : undefined,
  }
}

type MdtSymbol = "MDTb" | "MDTe" | "MDTu"

export interface BondStatistics {
  asset_id?: string
  bond_amount?: string
  mdtSymbol?: MdtSymbol
  icon_url?: string
}

export const useBondStatistics = () =>
  useQuery({
    queryKey: ["bond/statistics"],
    queryFn: async () => {
      let array = await client.get(`bonds/statistics`).json<BondStatistics[]>()

      array = array
        .map<BondStatistics>((item) => ({
          ...item,
          mdtSymbol: BOND_RATE_LIST.find((bond) => bond.id === item.asset_id)?.mdtSymbol,
        }))
        .filter((item) => item.mdtSymbol)

      return [
        ...array,
        {
          mdtSymbol: undefined, // Total
          bond_amount: formatNumber(bigAdd(...array.map((item) => item.bond_amount || "0")), { dp: 8 }),
        },
      ]
    },
  })

export const useTrace = (traceId: string, assetId: string, refetchInterval?: number, enabled?: boolean) =>
  useQuery({
    queryKey: ["trace", traceId, assetId],
    queryFn: () => {
      const isBond = BOND_RATE_LIST.find((item) => item.id === assetId)
      if (isBond) {
        return client.get(`bonds/trace/${traceId}?asset_id=${assetId}`).json()
      } else {
        return client.get(`snapshots/trace/${traceId}`).json()
      }
    },
    refetchInterval: refetchInterval,
    enabled: enabled,
  })

export const useBondState = (enabled: boolean) =>
  useQuery({
    queryKey: ["bond/state"],
    queryFn: () =>
      client.get(`bonds/state`).json<{
        allow_claim_xin: boolean
      }>(),
    enabled,
  })

export const useReceive = () =>
  useMutation({
    mutationFn: async (claim_xin: boolean) =>
      client
        .post("bonds/receive", {
          json: {
            claim_xin,
          },
        })
        .json(),
  })
